/**
 * Local-date helpers for PostgreSQL DATE values.
 *
 * DATE columns arrive as UTC ISO datetimes (e.g. "2026-04-05T00:00:00.000Z").
 * `new Date(iso)` interprets that as UTC midnight, which renders as the
 * PREVIOUS day for users west of UTC. Always parse the Y-M-D portion and
 * construct a local Date instead. Outbound dates must stay plain
 * `YYYY-MM-DD` strings (an <input type="date"> value passes through as-is —
 * never round-trip through toISOString()).
 */

/** Parse the date portion of an ISO datetime (or bare YYYY-MM-DD) as a local Date. */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.substring(0, 10).split('-');
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
}

/** Format a Date as a YYYY-MM-DD string using local calendar fields. */
export function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** "Today" / "Tomorrow" / "Sat, Apr 5" — matches the Expo slot-date format. */
export function formatSlotDate(dateStr: string): string {
  const date = parseLocalDate(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toLocaleDateString() === now.toLocaleDateString()) return 'Today';
  if (date.toLocaleDateString() === tomorrow.toLocaleDateString()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** True when the slot date falls before today (local midnight comparison). */
export function isPastDate(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parseLocalDate(dateStr) < today;
}
